/**
 * WeatherCalendarPanel · iter303ak-fix28 — Work Booked · T20 · Action 10
 * ──────────────────────────────────────────────────────────────────────
 * Split-panel:
 *   Left  · date picker (defaults to the earliest date in Accepted T10
 *           per_child_approx_starts). Operator sets work_planned_start_date.
 *   Right · MetService 7-day iframe for the property city.
 *           Falls back to "Open MetService" button if the site blocks embedding.
 *
 * Writes:
 *   work_planned_start_date (iso date)
 *   weather_review          { reviewed_at, city }
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Cloud, Calendar, ExternalLink, CheckCircle2, AlertTriangle } from 'lucide-react';

const METSERVICE_CITY_MAP = {
  // Common NZ city aliases → MetService location slugs.
  auckland: 'auckland',
  wellington: 'wellington',
  christchurch: 'christchurch',
  hamilton: 'hamilton',
  tauranga: 'tauranga',
  dunedin: 'dunedin',
  napier: 'napier',
  palmerston_north: 'palmerston-north',
  rotorua: 'rotorua',
  whangarei: 'whangarei',
  new_plymouth: 'new-plymouth',
  nelson: 'nelson',
  invercargill: 'invercargill',
  gisborne: 'gisborne',
};

function slugifyCity(city) {
  if (!city) return '';
  const norm = String(city).trim().toLowerCase().replace(/\s+/g, '-');
  const key = norm.replace(/-/g, '_');
  return METSERVICE_CITY_MAP[key] || norm;
}

function earliestFromApproxStarts(map) {
  if (!map || typeof map !== 'object') return null;
  // Prefer "urgent" label rows; otherwise use the top-of-order row.
  const rows = Object.values(map);
  if (!rows.length) return null;
  const sorted = [...rows].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  return sorted[0]?.suggested_iso || null;
}

export default function WeatherCalendarPanel({ action, capturedData, onNext, onBack, canBack, value, onChange }) {
  const params = action.params || {};
  const property = capturedData?.property || capturedData?.property_record || {};
  const city = property.suburb || property.city || property.town || '';
  const citySlug = slugifyCity(city);
  const metserviceUrl = citySlug
    ? `https://www.metservice.com/towns-cities/locations/${citySlug}`
    : 'https://www.metservice.com/';

  const approxStarts = capturedData?.per_child_approx_starts || {};
  const defaultDate = useMemo(() => {
    return value?.work_planned_start_date
      || earliestFromApproxStarts(approxStarts)
      || '';
  }, [approxStarts, value]);

  const [startDate, setStartDate] = useState(defaultDate ? defaultDate.slice(0, 10) : '');
  const [reviewedAt, setReviewedAt] = useState(value?.weather_review?.reviewed_at || null);
  const [iframeError, setIframeError] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (defaultDate && !startDate) {
      setStartDate(defaultDate.slice(0, 10));
    }
  }, [defaultDate]);   

  const setDate = (v) => {
    setStartDate(v);
    const iso = v ? new Date(`${v}T09:00:00`).toISOString() : '';
    onChange({
      work_planned_start_date: iso,
      weather_review: { reviewed_at: reviewedAt, city },
    });
  };

  const markReviewed = () => {
    const now = new Date().toISOString();
    setReviewedAt(now);
    const iso = startDate ? new Date(`${startDate}T09:00:00`).toISOString() : '';
    onChange({
      work_planned_start_date: iso,
      weather_review: { reviewed_at: now, city },
    });
  };

  const openMetservice = () => window.open(metserviceUrl, '_blank', 'noopener,noreferrer');

  return (
    <div>
      <div className="bg-gradient-to-br from-sky-50 to-white border-l-4 border-sky-400 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-sky-600 font-semibold mb-2">☀️ Weather + Start Date</div>
        <div className="text-lg text-slate-800 leading-relaxed" data-testid="wf-weather-prompt">
          {params.prompt || 'Pick the planned start date on the left; check the 7-day forecast on the right.'}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {/* Left · Date Picker */}
        <div className="border border-slate-200 rounded p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={16} className="text-slate-600" />
            <div className="text-sm font-semibold text-slate-700">Planned start date</div>
          </div>
          <input type="date" value={startDate} onChange={(e) => setDate(e.target.value)}
            data-testid="wf-weather-start-date"
            className="w-full px-3 py-2 border border-slate-300 rounded font-mono" />
          {defaultDate && (
            <div className="mt-2 text-[11px] text-slate-500">
              Default suggested from Accepted T10 per-child start times.
            </div>
          )}
          {!city && (
            <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>Property city missing — MetService location may be generic.</span>
            </div>
          )}
        </div>

        {/* Right · MetService iframe */}
        <div className="border border-slate-200 rounded p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Cloud size={16} className="text-slate-600" />
              <div className="text-sm font-semibold text-slate-700">
                MetService {city ? `· ${city}` : ''}
              </div>
            </div>
            <button onClick={openMetservice}
              data-testid="wf-metservice-open-btn"
              className="text-xs text-sky-600 hover:text-sky-800 inline-flex items-center gap-1">
              <ExternalLink size={12} /> Open
            </button>
          </div>
          <div className="border border-slate-200 rounded overflow-hidden bg-slate-50" style={{ height: 320 }}>
            {!iframeError ? (
              <iframe ref={iframeRef} src={metserviceUrl}
                title="MetService forecast"
                onError={() => setIframeError(true)}
                data-testid="wf-metservice-iframe"
                sandbox="allow-scripts allow-same-origin"
                className="w-full h-full" />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <AlertTriangle size={24} className="text-amber-500 mb-2" />
                <div className="text-sm text-slate-700 mb-2">MetService couldn&apos;t be embedded.</div>
                <button onClick={openMetservice}
                  className="px-3 py-1.5 bg-sky-600 text-white text-xs font-semibold rounded hover:bg-sky-700">
                  Open MetService in a new tab
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 p-4 mb-6 border-2 border-slate-200 rounded">
        <button onClick={markReviewed}
          data-testid="wf-weather-reviewed-btn"
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded hover:bg-emerald-700 flex items-center gap-2">
          <CheckCircle2 size={16} />
          {reviewedAt ? 'Reviewed ✓' : 'Mark weather reviewed'}
        </button>
        {reviewedAt && (
          <div className="text-xs text-slate-500">
            Reviewed at <span className="font-mono">{new Date(reviewedAt).toLocaleString()}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={onNext} disabled={!startDate || !reviewedAt}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">
          Next →
        </button>
      </div>
    </div>
  );
}
